import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { ArrowRight, Target, GraduationCap, Heart, Lightbulb, Users, Award, Calendar, MapPin, X } from "lucide-react";

function Home() {
  const [showEventBanner, setShowEventBanner] = useState(true);
  
  const stats = [
    { number: "500+", label: "Youth Served" },
    { number: "4", label: "Core Programs" },
    { number: "100%", label: "Community Focused" },
    { number: "Metro ATL", label: "Service Area" }
  ];

  const programHighlights = [
    {
      title: "STEAM Program",
      icon: <Lightbulb className="w-8 h-8" />,
      description: "Hands-on technology, engineering, and coding education",
      image: "/images/gallery/IMG_1354.jpg"
    },
    {
      title: "THOROpy",
      icon: <Heart className="w-8 h-8" />,
      description: "Mental health and confidence building through creative expression",
      image: "/images/gallery/DSC03803_VSCO.jpeg"
    },
    {
      title: "Bridge Program",
      icon: <GraduationCap className="w-8 h-8" />,
      description: "Trade certifications with guaranteed job placement",
      image: "/images/gallery/IMG_1373.jpg"
    }
  ];

  return (
    <div>
      {/* Event Banner - Move, Heal, Fuel */}
      {showEventBanner && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#1167B1] via-[#0d5390] to-[#1167B1] shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <Link to="/donate#move-heal-fuel" className="flex items-center gap-4 flex-1 hover:opacity-90 transition-opacity cursor-pointer">
                <div className="hidden sm:flex items-center gap-2 bg-[#FFE102] text-[#17181B] px-3 py-1 rounded-full font-bold text-sm">
                  <Award className="w-4 h-4" />
                  UPCOMING
                </div>
                <div className="flex-1">
                  <p className="text-white font-bold text-sm sm:text-base">
                    <span className="text-[#FFE102]">Move, Heal, Fuel</span> — Win $500 Scholarship!
                  </p>
                  <p className="text-white/80 text-xs sm:text-sm flex items-center gap-2">
                    <Calendar className="w-3 h-3" /> June 13 • 12-5PM
                    <span className="hidden sm:inline">•</span>
                    <MapPin className="w-3 h-3 hidden sm:inline" /> 
                    <span className="hidden sm:inline">Union City, GA</span>
                  </p>
                </div>
              </Link>
              <div className="flex items-center gap-2">
                <Link to="/donate#move-heal-fuel">
                  <Button className="bg-[#FFE102] text-[#17181B] hover:bg-[#FFE102]/90 font-bold text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-full">
                    Donate Now
                  </Button>
                </Link>
                <button 
                  onClick={() => setShowEventBanner(false)}
                  className="text-white/70 hover:text-white p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className={`${showEventBanner ? 'pt-44 sm:pt-40' : 'pt-32'} pb-20 px-4 bg-gradient-to-br from-[#1167B1] via-[#1167B1] to-[#0d5390] transition-all`} data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-white space-y-6">
              <Badge className="bg-[#FFE102] text-[#17181B] hover:bg-[#FFE102]/90 text-sm font-bold px-4 py-2">
                Empowering Youth • Building Futures
              </Badge>
              <h1 className="text-5xl md:text-6xl font-bold leading-tight">
                Transforming Lives Through Education & Community
              </h1>
              <p className="text-xl text-white/90">
                Calvin D. Butts Inc. provides STEAM education, mental health support, and workforce development to Metro Atlanta youth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/donate">
                  <Button className="bg-[#FFE102] text-[#17181B] hover:bg-[#FFE102]/90 font-bold text-lg px-8 py-6 rounded-full shadow-xl">
                    Make a Donation <ArrowRight className="ml-2" />
                  </Button>
                </Link>
                <Link to="/programs">
                  <Button variant="outline" className="bg-white text-[#1167B1] hover:bg-white/90 font-bold text-lg px-8 py-6 rounded-full border-2">
                    Explore Programs
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <img src="/images/gallery/image(1).jpg" alt="Youth engagement" className="rounded-2xl shadow-2xl w-full h-48 object-cover" />
                <img src="/images/gallery/IMG-8210.JPG" alt="STEAM activities" className="rounded-2xl shadow-2xl w-full h-48 object-cover" />
                <img src="/images/gallery/IMG_1454.jpg" alt="THOROpy program" className="rounded-2xl shadow-2xl w-full h-48 object-cover" />
                <img src="/images/gallery/IMG_1359.jpg" alt="Community programs" className="rounded-2xl shadow-2xl w-full h-48 object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Statement */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <Target className="w-12 h-12 mx-auto mb-4 text-[#1167B1]" />
            <Badge className="bg-[#FFE102] text-[#17181B] font-bold mb-6">Our Impact</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-[#17181B] mb-8">Creating Life-Changing Opportunities</h2>
          </div>
          <Card className="border-2 border-[#1167B1] shadow-2xl">
            <CardContent className="p-8 md:p-12">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed text-center">
                Calvin D. Butts Inc. creates <span className="font-bold text-[#1167B1]">life-changing opportunities</span> for underserved and justice-impacted youth by combining innovative <span className="font-bold text-[#1167B1]">STEAM education</span>, leadership training, and <span className="font-bold text-[#1167B1]">mental-wellness programming</span> with intentional community partnerships. Our work equips young people with confidence, employable skills, and a network of support that empowers them to <span className="font-bold text-[#1167B1]">break cycles of hardship</span> and build sustainable futures. Through therapeutic arts, mentorship, and our mental-health–centered apparel line, we not only raise awareness but <span className="font-bold text-[#1167B1]">reinvest resources directly back</span> into the youth we serve—impacting families, schools, and entire communities across <span className="font-bold text-[#1167B1]">Metro Atlanta and beyond</span>.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-16 bg-[#17181B]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-[#FFE102] mb-2">{stat.number}</div>
                <div className="text-white/80 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Highlights */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#1167B1] text-white mb-4">Our Programs</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-[#17181B] mb-4">Building Futures, One Student at a Time</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Explore our comprehensive programs designed to empower youth.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {programHighlights.map((program, index) => (
              <Card key={index} className="border-2 hover:border-[#1167B1] transition-all hover:shadow-xl overflow-hidden">
                <div className="h-48 overflow-hidden">
                  <img src={program.image} alt={program.title} className="w-full h-full object-cover hover:scale-110 transition-transform duration-300" />
                </div>
                <CardContent className="p-6">
                  <div className="w-16 h-16 bg-[#1167B1] text-white rounded-full flex items-center justify-center mb-4">
                    {program.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-[#17181B] mb-2">{program.title}</h3>
                  <p className="text-gray-600">{program.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Link to="/programs">
              <Button className="bg-[#1167B1] text-white hover:bg-[#0d5390] font-bold text-lg px-8 py-6 rounded-full">
                View All Programs
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-[#1167B1]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <Users className="w-16 h-16 mx-auto mb-6 text-[#FFE102]" />
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Make an Impact Today</h2>
          <p className="text-xl mb-8 text-white/90">
            Your donation directly supports STEAM education, mental health services, and career development for Metro Atlanta youth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/donate">
              <Button className="bg-[#FFE102] text-[#17181B] hover:bg-[#FFE102]/90 font-bold text-xl px-12 py-7 rounded-full shadow-2xl">
                DONATE NOW <Heart className="ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="bg-white text-[#1167B1] hover:bg-white/90 font-bold text-xl px-12 py-7 rounded-full border-2">
                Get Involved
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;